/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setPhotos, setLookingFor, setInterests, setBio } from '../../store/slices/authSlice';
import { Search } from 'react-feather';
import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getLookingForOptions } from '../../utils/lookingForOptions';
import { userService } from '../../services/userService';
import ImageCropper from '../common/ImageCropper';
import Spinner from '../common/Spinner';
import { mediaService } from '../../services/mediaService';

const EditProfile = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const authState = useSelector(state => state.auth);
    const { t } = useTranslation();
    const [images, setImages] = useState(Array(6).fill(null));
    const [, setError] = useState(null);
    const [uploadingIndex, setUploadingIndex] = useState(null);
    const [selectedInterests, setSelectedInterests] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [bio, setBioState] = useState('');
    const [bioError, setBioError] = useState('');
    const [cropData, setCropData] = useState(null);

    // Charger les photos existantes
    useEffect(() => {
        const newImages = Array(6).fill(null);
        if (authState.photos && authState.photos.length > 0) {
            authState.photos.forEach((photo, index) => {
                if (index < 6) {
                    newImages[index] = {
                        url: photo.photoUrl,
                        objectKey: photo.objectKey,
                        public_id: photo.public_id || photo.publicId
                    };
                }
            });
        }
        setImages(newImages);
    }, [authState.photos]);

    // Charger les intérêts existants
    useEffect(() => {
        if (authState.interests) {
            setSelectedInterests(authState.interests.split(','));
        }
    }, [authState.interests]);

    // Charger la bio existante
    useEffect(() => {
        if (authState.bio) {
            setBioState(authState.bio);
        }
    }, [authState.bio]);

    // Function to update server and Redux
    const updateUserField = async (fieldName, value) => {
        setIsUpdating(true);
        try {
            const updatedProfile = {[fieldName]: value };
            await userService.updateProfile(authState.uid, updatedProfile);

            // If server update successful, update Redux
            switch(fieldName) {
                case 'LookingFor':
                    dispatch(setLookingFor(value));
                    break;
                case 'interests':
                    dispatch(setInterests(value));
                    break;
                case 'photos':
                    dispatch(setPhotos(value));
                    break;
                case 'bio':
                    dispatch(setBio(value));
                    break;
                // Add other cases as needed
                default:
                    break;
            }
        } catch (error) {
            console.error('Error updating user field:', error);
            setError('Failed to update. Please try again.');
            throw error; // Re-throw to handle in calling function
        } finally {
            setIsUpdating(false);
        }
    };

    const handleImageUpload = async (event, index) => {
        const file = event.target.files[0];
        if (!file) return;

        // Créer URL pour l'image à cropper
        const imageUrl = URL.createObjectURL(file);
        setCropData({
            imageUrl,
            index,
            fileName: file.name
        });
    };

    const handleCropComplete = async (croppedFile) => {
        const index = cropData.index;
        const previousImage = images[index];
        let uploadedImage = null;
        let profileUpdated = false;
        setUploadingIndex(index);
        try {
            // Upload vers Cloudflare R2
            uploadedImage = await mediaService.uploadImage(croppedFile);
            
            // Préparer le nouvel état, puis attendre la sauvegarde en base
            const newImages = [...images];
            newImages[index] = {
                url: uploadedImage.photoUrl,
                objectKey: uploadedImage.objectKey
            };
      
            const photos = newImages
                .map(photo => photo ? {
                    photoUrl: photo.url,
                    objectKey: photo.objectKey,
                    ...(photo.public_id ? { public_id: photo.public_id } : {})
                } : null)
                .filter(Boolean);
   
            // Update our server
            await userService.updateProfile(authState.uid, {photos: photos});
            profileUpdated = true;

            setImages(newImages);
            dispatch(setPhotos(photos));

            if (previousImage?.objectKey) {
                mediaService.deleteImage(previousImage.objectKey).catch(cleanupError => {
                    console.error('Error deleting replaced image:', cleanupError);
                });
            }

        } catch (error) {
            console.error('Error handling cropped image:', error);
            if (uploadedImage?.objectKey && !profileUpdated) {
                mediaService.deleteImage(uploadedImage.objectKey).catch(cleanupError => {
                    console.error('Error cleaning up uploaded image:', cleanupError);
                });
            }
            setError(t('editProfile.errors.uploadFailed'));
        } finally {
            setUploadingIndex(null);
            setCropData(null);
        }
    };

    const handleCropCancel = () => {
        setCropData(null);
    };

    const removeImage = async (index) => {
        const imageToRemove = images[index];
        if (!imageToRemove) return;

        setUploadingIndex(index);
        try {
            const newImages = [...images];
            newImages[index] = null;
            
            const photos = newImages
                .map(photo => photo ? {
                    photoUrl: photo.url,
                    objectKey: photo.objectKey,
                    ...(photo.public_id ? { public_id: photo.public_id } : {})
                } : null)
                .filter(Boolean);

            // Update server first
            await userService.updateProfile(authState.uid, {photos: photos});

            setImages(newImages);
            dispatch(setPhotos(photos));

            if (imageToRemove.objectKey) {
                await mediaService.deleteImage(imageToRemove.objectKey);
            }

        } catch (error) {
            console.error('Error removing image:', error);
            setError(t('editProfile.errors.deleteFailed'));
        } finally {
            setUploadingIndex(null);
        }
    };

    const confirmRemoveImage = (index) => {
        const currentPhotoCount = images.filter(Boolean).length;
        if (currentPhotoCount <= 2) {
            Modal.warning({
                title: t('editProfile.deletePhoto.minimumTitle'),
                content: t('editProfile.errors.minimumPhotosRequired'),
                okText: t('editProfile.deletePhoto.close')
            });
            return;
        }

        Modal.confirm({
            title: t('editProfile.deletePhoto.confirmTitle'),
            icon: <ExclamationCircleOutlined />,
            content: t('editProfile.deletePhoto.confirmMessage'),
            okText: t('editProfile.deletePhoto.confirmButton'),
            okButtonProps: { danger: true },
            cancelText: t('editProfile.deletePhoto.cancelButton'),
            onOk: () => removeImage(index)
        });
    };

    // Options pour les objectifs relationnels
    const lookingForOptions = getLookingForOptions(t);

    const handleLookingForChange = (selectedOption) => {
        updateUserField('LookingFor', selectedOption.value);
    };

    const interestsList = [
        t('interests.sport'), t('interests.music'), 
        t('interests.travel'), t('interests.reading'), 
        t('interests.cinema'), t('interests.art'),
        t('interests.photography'), t('interests.cooking'), 
        t('interests.dance'), t('interests.gaming'), 
        t('interests.fashion'), t('interests.technology'), 
        t('interests.nature'), t('interests.fitness'), 
        t('interests.yoga'), t('interests.meditation'),
        t('interests.theater'), t('interests.concert'), 
        t('interests.festival'), t('interests.hiking'), 
        t('interests.beach'), t('interests.shopping'), 
        t('interests.volunteering'), t('interests.languages')
    ];

    const toggleInterest = async (interest) => {
        let newInterests;
        if (selectedInterests.includes(interest)) {
            newInterests = selectedInterests.filter(i => i !== interest);
        } else {
            newInterests = [...selectedInterests, interest];
        }
        setSelectedInterests(newInterests);
        await updateUserField('interests', newInterests.join(','));
    };

    const getSelectedInterestsLabel = () => {
        if (!selectedInterests.length) return t('editProfile.interests.selectPlaceholder');
        if (selectedInterests.length <= 2) return selectedInterests.join(', ');
        return t('editProfile.interests.moreLabel', {
            first: selectedInterests.slice(0, 2).join(', '),
            remaining: selectedInterests.length - 2
        });
    };

    return (
        <div className="page-wrapper edit-profile-page">
                {/* Preloader */}
                <div id="preloader">
                    <div className="loader">
                        <div className="load-circle"><div></div><div></div></div>
                    </div>
                </div>

                {/* Header */}
                <header className="header header-fixed bg-white border-0 style-2">
                    <div className="container">
                        <div className="header-content">
                            <div className="left-content me-3">
                                <button onClick={() => navigate(-1)} className="back-btn">
                                    <i className="icon feather icon-chevron-left"></i>
                                </button>
                                <h6 className="title">{t('editProfile.title')}</h6>
                            </div>
                            <div className="mid-content">
                            </div>
                            <div className="right-content">
                            </div>
                        </div>
                    </div>
                </header>
                {(isUpdating || uploadingIndex !== null) && <Spinner />}
            {cropData && (
                <ImageCropper
                    image={cropData.imageUrl}
                    fileName={cropData.fileName}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                />
            )}
                {/* Contenu de la page */}
            <div className="content-body space-top">
                <div className="container">
                    <div className="row g-2 m-b30">
                        {images.map((image, index) => (
                            <div key={index} className="col-4 image-upload-col"> 
                                <div className={`dz-drop-box style-2 ${!image ? 'empty' : ''}`}>
                                <img src="../assets/images/recent-pic/drop-bx2.png" alt=""/>
                                    <div className="drop-bx">
                                

                                        <div className={`imagePreview ${image ? 'active' : ''}`} 
                                             style={image ? {backgroundImage: `url(${image.url})`} : {}}>
                                      <label htmlFor={`imageUpload${index}`} className="add-btn">
                                            <i className="icon feather icon-plus"></i>
                                            </label>
                                            {image && images.filter(img => img !== null).length > 2 && (
                                                <button
                                                  type="button"
                                                  className="remove-img remove-btn"
                                                  onClick={() => confirmRemoveImage(index)}
                                                  aria-label={t('editProfile.deletePhoto.confirmButton')}
                                                  disabled={uploadingIndex !== null}
                                                >
                                                  <i className="icon feather icon-x"></i>
                                                </button>
                                            )}
                                            {uploadingIndex === index ? (
                                                <div className="upload-loading">
                                                    <div className="load-circle">
                                                        <div></div>
                                                        <div></div>
                                                    </div>
                                                    <span>Uploading...</span>
                                                </div>
                                            ) : (
                                                <input
                                                    type='file'
                                                    className="form-control d-none imageUpload"
                                                    id={`imageUpload${index}`}
                                                    accept=".png, .jpg, .jpeg"
                                                    onChange={(e) => handleImageUpload(e, index)}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Card de la bio */}
                    <div className="card style-4 mt-3">
                        <div className="card-header">
                            <h6 className="title mb-0">{t('editProfile.bio.title')}</h6>
                        </div>
                        <div className="card-body bio">
                            <div className="form-group">
                                <textarea
                                    className="form-control"
                                    rows="4"
                                    value={bio}
                                    placeholder={t('editProfile.bio.placeholder')}
                                    onChange={(e) => {
                                        const text = e.target.value;
                                        if (text.length <= 500) {
                                            setBioState(text);
                                            setBioError('');
                                        } else {
                                            setBioError(t('editProfile.bio.maxLengthError'));
                                        }
                                    }}
                                    onBlur={() => {
                                        if (bio !== authState.bio) {
                                            updateUserField('bio', bio);
                                        }
                                    }}
                                ></textarea>
                                <div className="d-flex justify-content-between mt-2">
                                    <small className="text-danger">{bioError}</small>
                                    <small className="text-muted">{bio.length}/500</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Card des objectifs relationnels */}
                    <div className="card style-4">
                        <div className="card-header">
                            <h6 className="title mb-0">{t('editProfile.lookingFor.title')}</h6>
                        </div>
                        <div className="card-body">
                            <a onClick={() => {}} 
                               className="setting-input" 
                               data-bs-toggle="offcanvas" 
                               data-bs-target="#offcanvasRelationship" 
                               aria-controls="offcanvasRelationship"
                            >
                                <span>{lookingForOptions.find(option => option.value === authState.LookingFor)?.label}</span>
                                <i className="icon feather icon-chevron-right ms-auto me-0"></i>
                            </a>
                        </div>
                    </div>

                    {/* Offcanvas pour la sélection d'objectif */}
                    <div className="offcanvas offcanvas-bottom" id="offcanvasRelationship">
                        <div className="offcanvas-header">
                            <h5 className="offcanvas-title">{t('editProfile.lookingFor.title')}</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close">
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                        <div className="offcanvas-body">
                            <div className="radio style-4 row g-2">
                                {lookingForOptions.map((option) => (
                                    <div key={option.value} className="col-4">
                                        <label className="radio-label">
                                            <input
                                                type="radio"
                                                name="lookingFor"
                                                checked={authState.LookingFor === option.value}
                                                onChange={() => handleLookingForChange(option)}
                                            />
                                            <span className="checkmark">
                                                <img src={option.icon} alt="" />                         
                                                <span className="text">{option.label}</span>  
                                            </span>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Card des langues */}
                    {/* <div className="card style-4">
                        <div className="card-header">
                            <h6 className="title mb-0">Langues</h6>
                        </div>
                        <div className="card-body">
                            <a onClick={() => {}} className="setting-input" data-bs-toggle="offcanvas" data-bs-target="#offcanvasLang" aria-controls="offcanvasLang">
                                <span>Sélectionner vos langues</span>
                                <i className="icon feather icon-chevron-right ms-auto me-0"></i>
                            </a>
                        </div>
                    </div> */}

                    {/* Card des centres d'intérêt */}
                    <div className="card style-4">
                        <div className="card-header">
                            <h6 className="title mb-0">{t('editProfile.interests.title')}</h6>
                        </div>
                        <div className="card-body">
                            <a onClick={() => {}} 
                               className="setting-input" 
                               data-bs-toggle="offcanvas" 
                               data-bs-target="#offcanvasInterests" 
                               aria-controls="offcanvasInterests"
                            >
                                <span>{getSelectedInterestsLabel()}</span>
                                <i className="icon feather icon-chevron-right ms-auto me-0"></i>
                            </a>
                        </div>
                    </div>

                    {/* Offcanvas pour la sélection des centres d'intérêt */}
                    <div className="offcanvas offcanvas-bottom" id="offcanvasInterests">
                        <div className="offcanvas-header">
                            <h5 className="offcanvas-title">{t('editProfile.interests.title')}</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close">
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                        <div className="offcanvas-body">
                            <div className="interests-grid">
                                {interestsList.map((interest) => (
                                    <div 
                                        key={interest}
                                        className={`interest-item ${selectedInterests.includes(interest) ? 'active' : ''}`}
                                        onClick={() => toggleInterest(interest)}
                                    >
                                        {interest}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Offcanvas pour la sélection des langues */}
                    <div className="offcanvas offcanvas-bottom" tabIndex="-1" id="offcanvasLang">
                        <div className="offcanvas-header share-style">
                            <h6 className="title mb-0">Langues</h6>
                            <button type="button" className="btn-close dz-flex-box" data-bs-dismiss="offcanvas" aria-label="Close">
                                <i className="icon feather icon-x font-22"></i>
                            </button>
                        </div>
                        <div className="offcanvas-body">
                            <div className="dz-list">
                                <ul className="mb-2 confirm-lang">
                                    {[
                                        { lang: 'Français', flag: 'france.svg' },
                                        { lang: 'Anglais', flag: 'united-states.svg' },
                                        { lang: 'Espagnol', flag: 'spain.svg' },
                                        { lang: 'Allemand', flag: 'germany.svg' },
                                        { lang: 'Italien', flag: 'italy.svg' }
                                    ].map((item, index) => (
                                        <li key={index} data-lang={item.lang}>
                                            <a onClick={() => {}} className="item-content py-2 item-link">
                                                <div className="media media-30 me-3">
                                                    <img src={`../assets/images/flags/${item.flag}`} alt="/" />
                                                </div>
                                                <div className="dz-inner">
                                                    <span className="title">{item.lang}</span>
                                                </div>
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Centres d'intérêts OffCanvas */}
                    <div className="offcanvas offcanvas-bottom intrests" tabIndex="-1" id="offcanvasBottom1">
                        <div className="offcanvas-header share-style">
                            <h6 className="title mb-0">Centres d'intérêts</h6>
                            <button type="button" className="btn-close dz-flex-box" data-bs-dismiss="offcanvas" aria-label="Close">
                                <i className="icon feather icon-x font-22"></i>
                            </button>
                        </div>
                        <div className="offcanvas-body">
                            <div className="input-group input-group-icon search-input">
                                <div className="input-group-text">
                                    <div className="input-icon">
                                        <Search />
                                    </div>
                                </div>
                                <input type="search" className="form-control ms-0" placeholder="Rechercher..." />
                            </div>
                            <ul className="dz-tag-list style-2">
                                {['Sport', 'Musique', 'Cinéma', 'Lecture', 'Voyage', 'Cuisine', 'Art', 'Photographie', 'Danse', 'Technologie'].map((interest, index) => (
                                    <li key={index}> 
                                        <div className="dz-tag">
                                            <span>{interest}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditProfile;
